import { useContext, useEffect, useState } from 'react';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { ILearnActionService } from '@intigral/domain/lib/application/services/LearnActionService';
import { useMediaQuery } from '@intigral/hooks';
import { capitalizeFirstLetter } from '@intigral/utils/lib/helpers/string';
import { FontIcon, LikeDislike, Text } from '@intigral/shared';
import { EVENT, KEYS } from '../../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getMixpanelLinearProperties, getMixpanelVodProperties } from '../../../contexts/MixpanelHelper';
import h, { getCountry } from '../../../helpers/common';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import playerStyles from '@intigral/shared/styles/components/bitmovin.module.sass';
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import url from '../../../helpers/url';

// todo: move to `shared` UI components?
interface IHeader {
    /** If true `like`/`dislike` buttons are shown */
    isLikeDislikeEnabled?: boolean;
    /** Asset (VOD, Linear) data - guid, category, etc. */
    content?: {
        guid: string;
        category: string;
    };
    videoError?: boolean;
    headerText?: string;
    closePlayer: () => void;
}

type ContentLiked = 'like' | 'dislike';

/**
 * VOD player header containing asset title, player close button and optional like/dislike buttons
 */
const Header = ({ content, headerText, closePlayer, isLikeDislikeEnabled, videoError }: IHeader) => {
    const [isContentLiked, setContentLiked] = useState<ContentLiked | null>(null);
    const isMobile = useMediaQuery('(max-width: 924px)');
    const { track } = useContext(MixpanelContext);

    useEffect(() => {
        getContentRating();
    }, [content?.guid]);

    // TODO: use LikeDislikeButton component - it contains all the necessary logic - getContentRating, handleLikeDislike, etc.
    /**
     * Tracks a Mixpanel event for adding or removing a content rating.
     *
     * @param {string} actionType - The type of action being performed (e.g., "like", "dislike").
     * @param {boolean} [isRemove=false] - Indicates whether the action is a removal of a rating (like/dislike).
     *
     * The event will be either for adding or removing rating based on the `isRemove` flag.
     * Additional properties are added to the event based on the content category - VOD or linear.
     */
    const addMixpanelEvent = (actionType: string, isRemove = false) => {
        track(isRemove ? EVENT.CONTENT_RATING.CONTENT_RATING_REMOVE : EVENT.CONTENT_RATING.CONTENT_RATING_ADD, {
            [KEYS.GENERAL.RATING_TYPE]: capitalizeFirstLetter(actionType),
            ...(content.category === 'VOD' ? getMixpanelVodProperties(content) : getMixpanelLinearProperties(content)),
        });
    };

    /**
     * Fetches the rating - like/dislike - for the current user/content (VOD, Series) and updates the state.
     *
     * @param {boolean} [triggerMixPanelEvent=false]
     * @param {string} [clickActionType=''] - The type of action to log in MixPanel if an event is triggered.
     *
     * If an error occurs during the fetch, it logs a warning and sets the content rating state to null.
     */
    const getContentRating = (triggerMixPanelEvent = false, clickActionType = '') => {
        if (content?.guid) {
            const searchParams = {
                userName: user.getUsername(),
                profileName: user.getProfileName(),
                itemGuid: content?.guid,
            };
            const learnActionService = container.resolve<ILearnActionService>(DI.SERVICES.ILearnActionService);
            learnActionService
                .getContentRating(user.getAPIKey(), searchParams, user.getIdentityToken())
                .then((_res) => {
                    let value = null;
                    if (_res?.isLike === true) {
                        value = 'like';
                    } else if (_res?.isLike === false) {
                        value = 'dislike';
                    }
                    if (triggerMixPanelEvent) {
                        addMixpanelEvent(clickActionType, _res === null ? true : false);
                    }
                    setContentLiked(value);
                })
                .catch((_error) => {
                    setContentLiked(null);
                });
        }
    };

    /**
     * Updates like/dislike component state and sends a request to backend for updating like/dislike for current asset
     * (content)
     */
    const handleLikeDislike = (actionType: ContentLiked = 'like') => {
        if (content?.guid) {
            setContentLiked(actionType === isContentLiked ? null : actionType);
            const searchParams = {
                userName: user.getUsername(),
                profileName: user.getProfileName(),
                itemGuid: content?.guid,
                action: actionType,
                device: h.getDevice(),
                country: getCountry(),
            };
            const learnActionService = container.resolve<ILearnActionService>(DI.SERVICES.ILearnActionService);
            learnActionService.addLikeDislike(user.getAPIKey(), searchParams, user.getIdentityToken()).then(() => {
                getContentRating(true, actionType);
            });
        }
    };

    const navigate = (url.navigate = useNavigate());

    return (
        <>
            {isLikeDislikeEnabled && (
                <LikeDislike
                    likeLabel={__('like_content')}
                    dislikeLabel={__('dislike_content')}
                    isContentLiked={isContentLiked}
                    onActionClick={handleLikeDislike}
                />
            )}
            <div
                className={playerStyles.title}
                ref={(el) => {
                    if (el) {
                        if (videoError || isMobile) {
                            el.style.setProperty('width', '0px', 'important');
                        } else {
                            el.style.setProperty('width', 'auto', 'important');
                        }
                    }
                }}
            >
                <Text as="span" color="white">
                    {headerText}
                </Text>
            </div>
            <div className={playerStyles.holder}>
                <a
                    className={playerStyles.close}
                    onClick={() => {
                        closePlayer();
                    }}
                >
                    <FontIcon name="close" size="small" md="small" />
                </a>
            </div>
        </>
    );
};

export default Header;
